import { memo, useCallback, useMemo } from "react";
import { Stripe, StripeElements } from "@stripe/stripe-js";
import { ExpressCheckoutWithCardForm } from "./ExpressCheckoutWithCardForm";
import { useHookstate } from "@hookstate/core";
import { appStore } from "../../../hookstate/appStore";
import { InitResponsePayloadResults } from "../../../services/gc/types";

type EmbeddedStripePaymentFormProps = {
  submitButtonText: string;
  disabled?: boolean;
  onComplete: (confirmationTokenId: string) => void;
};

export const EmbeddedStripePaymentForm = memo(
  ({
    submitButtonText,
    disabled,
    onComplete,
  }: EmbeddedStripePaymentFormProps) => {
    const initData = useHookstate(appStore.init).get({
      noproxy: true,
    }) as InitResponsePayloadResults & {
      guest_setup_payment_method_configuration_id?: string | null;
    };

    // setup mode keeps the same Stripe Elements instance usable for one-time
    // and recurring flows: the resulting ConfirmationToken can confirm a
    // SetupIntent server-side (which attaches the PM to the customer), and the
    // backend then either charges off-session (one-time) or creates a
    // Subscription (recurring). The user's typed card details aren't lost when
    // a sibling toggle (e.g., "Repeat Monthly") flips on this form.
    const elementsOptions = useMemo(
      () => {
        const paymentMethodConfiguration =
          initData.guest_setup_payment_method_configuration_id;

        return paymentMethodConfiguration
          ? {
              mode: "setup" as const,
              currency: "usd",
              paymentMethodConfiguration,
            }
          : {
              mode: "setup" as const,
              currency: "usd",
              paymentMethodTypes: ["card", "link"],
            };
      },
      [initData.guest_setup_payment_method_configuration_id],
    );

    const onConfirm = useCallback(
      async ({
        stripe,
        elements,
      }: {
        stripe: Stripe;
        elements: StripeElements;
      }) => {
        const submitResult = await elements.submit();
        if (submitResult.error) {
          throw new Error(
            submitResult.error.message ?? "Unable to verify payment method.",
          );
        }

        const result = await stripe.createConfirmationToken({ elements });
        if (result.error) {
          throw new Error(
            result.error.message ?? "Unable to verify payment method.",
          );
        }

        if (!result.confirmationToken?.id) {
          throw new Error("Unable to verify payment method. Please try again.");
        }

        onComplete(result.confirmationToken.id);
      },
      [onComplete],
    );

    return (
      <ExpressCheckoutWithCardForm
        elementsOptions={elementsOptions}
        onConfirm={onConfirm}
        submitButtonText={submitButtonText}
        submitTestId="btnSubmitPayment"
        disabled={disabled}
      />
    );
  },
);
